let first_time = Date.now();
let last_time = Date.now();


export default {
	
	/**
	 * Throttle
	 * @param {function} callback
	 * @param {number} interval
	 * @param {object} options
	 * @param {boolean} options.leading
	 * @param {boolean} options.trailing
	 * @return {function}
	 */
	throttle(callback, interval, { leading = true, trailing = true } = {}) {
		let last_call = 0;
		let timeout = null;
		let last_args = null;
		let last_this = null;
		
		const invoke = () => {
			last_call = Date.now();
			timeout = null;
			callback.apply(last_this, last_args);
		};
		
		const throttled = function (...args) {
			const now = Date.now();
			const remaining = interval - (now - last_call);
			last_args = args;
			last_this = this;
			
			if (remaining <= 0 || remaining > interval) {
				if (timeout) {
					clearTimeout(timeout);
					timeout = null;
				}
				last_call = now;
				if (leading) callback.apply(this, args);
			} else if (!timeout && trailing) {
				timeout = setTimeout(invoke, remaining);
			}
		};
		
		throttled.cancel = () => {
			clearTimeout(timeout);
			timeout = null;
			last_call = 0;
			last_args = null;
			last_this = null;
		};
		
		return throttled;
	},
	
	
	/**
	 * Debounce
	 * @param {function} callback
	 * @param {number} delay
	 * @param {object} options
	 * @param {boolean} options.leading
	 * @param {boolean} options.trailing
	 * @return {function}
	 */
	debounce(callback, delay, { leading = false, trailing = true } = {}) {
		let timeout = null;
		let last_args = null;
		let last_this = null;
		
		const debounced = function (...args) {
			last_args = args;
			last_this = this;
			const call_now = leading && !timeout;
			
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				timeout = null;
				if (trailing) callback.apply(last_this, last_args);
			}, delay);
			
			if (call_now) callback.apply(this, args);
		};
		
		debounced.cancel = () => {
			clearTimeout(timeout);
			timeout = null;
			last_args = null;
			last_this = null;
		};
		
		return debounced;
	},
	
	
	/**
	 * Does this device support touch input?
	 * @return {boolean}
	 */
	isTouch() {
		return (
			('ontouchstart' in window)
			|| (navigator.maxTouchPoints > 0)
		);
	},
	
	
	/**
	 * Get a query string value by key
	 * @param {string} key
	 * @return {string|null}
	 */
	get_query(key) {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.get(key);
	},
	
	
	/**
	 * Log with elapsed time
	 * @param {mixed} arguments
	 */
	log() {
		const time_since_first = elapsed_first().toString().padStart(5, ' ');
		let time_since_last = elapsed_last();
		Array.from(arguments).forEach(data => {
			time_since_last = time_since_last.toString().padStart(5, ' ');
			if (typeof data !== 'object') {
				console.log(`${time_since_first} / ${time_since_last}: ${data}`);
			} else {
				console.log(`${time_since_first} / ${time_since_last}:`, data);
			}
			time_since_last = '';
		});
	},
	
	
	/**
	 * Is this valid JSON?
	 * @param {string} text
	 * @return {boolean}
	 */
	isValidJSON(text) {
		try {
			JSON.parse(text);
			return true;
		} catch {
			return false;
		}
	},
	
	
	/**
	 * Is this CSS selector valid?
	 * @param {string} selector
	 * @return {boolean}
	 */
	isValidSelector(selector) {
		try {
			document.createDocumentFragment().querySelector(selector);
			return true;
		} catch {
			return false;
		}
	},
	
	
	/**
	 * Get furthest ancestor matching selector
	 * @param {element} $element
	 * @param {string} selector
	 * @return {element}
	 */
	furthest($element, selector) {
		let $match = null;
		let $current = $element;
		
		while ($current) {
			if ($current.matches(selector)) {
				$match = $current;
			}
			$current = $current.parentElement;
		}
		return $match;
	},
	
	
	/**
	 * Get current URL
	 * @return {string}
	 */
	currentURL() {
		return window.location.protocol
			+ '//'
			+ window.location.hostname
			+ (window.location.port ? ':' + window.location.port : '')
			+ window.location.pathname
		;
	},
	
	
	/**
	 * Generate random ID
	 * @param {int} digits
	 * @param {boolean} as_string
	 * @return {int|string}
	 */
	randomID(digits = 5, as_string = true) {
		const min = (!as_string)
			? Math.pow(10, digits - 1)
			: 0;
		const max = Math.pow(10, digits) - 1;
		const id = Math.floor(Math.random() * (max - min + 1)) + min;
		return (as_string)
			? id.toString().padStart(digits, '0')
			: id;
	},
	
	
	kebab(string) {
		return this.mechanize(string, '-');
	},
	chain(string) {
		return this.mechanize(string, '-');
	},
	snake(string) {
		return this.mechanize(string, '_');
	},
	compact(string) {
		return this.mechanize(string, '');
	},
	
	
	/**
	 * Mechanize
	 * @param {string} string
	 * @param {string} separator
	 * @return {string}
	 */
	mechanize(string, separator = '_') {
		if (!string.length) return '';
		
		string = string.toLowerCase();
		string = this.transliterate(string);
		string = string
			.replace(/['’]/g, '')
			.replace(/[^a-z0-9\s_]/g, '_')
			.replace(/[\s_]+/g, '_')
			.replace(/^_|_$/g, '')
		;
		if (separator !== '_') {
			string = string.replace(/_/g, separator);
		}
		return string;
	},
	
	
	/**
	 * Transliterate
	 * @param {string} string
	 * @return {string}
	 */
	transliterate(string) {
		const transliterations = {
			a: 'аáăâäàāąåãǻǎ',
			ya: 'я',
			ae: 'æǽ',
			b: 'б',
			c: 'ћćčçĉċ',
			ch: 'ч',
			d: 'дðďđ',
			dzh: 'џ',
			dz: 'ѕ',
			dj: 'ђ',
			e: 'еэéĕěêëėèēę',
			ye: 'є',
			f: 'ф',
			g: 'гґğĝģġ',
			h: 'ħĥ',
			i: 'иіıíĭîïìīįĩǐ',
			yi: 'ї',
			ij: 'ĳ',
			j: 'йȷĵ',
			k: 'кķ',
			kh: 'х',
			l: 'лĺľļŀł',
			lj: 'љ',
			m: 'м',
			n: 'нńňņñ',
			nj: 'њ',
			ng: 'ŋ',
			o: 'оóŏôöòőōøõǿǒơ',
			yo: 'ё',
			oe: 'œ',
			p: 'п',
			r: 'рŕřŗ',
			s: 'сśšşŝș',
			ss: 'ß',
			sh: 'ш',
			shch: 'щ',
			t: 'тŧťţț',
			ts: 'ц',
			th: 'þ',
			u: 'уúŭûüùűūųůũǔǖǘǚǜư',
			yu: 'ю',
			v: 'в',
			w: 'ўẃŵẅẁ',
			y: 'ыýŷÿỳ',
			z: 'зźžż',
			zh: 'ж',
			
			A: 'АÁĂÂÄÀĀĄÅÃǺǍ',
			Ya: 'Я',
			Ae: 'ÆǼ',
			B: 'Б',
			C: 'ЋĆČÇĈĊ',
			Ch: 'Ч',
			D: 'ДÐĎĐ',
			Dzh: 'Џ',
			Dz: 'Ѕ',
			Dj: 'Ђ',
			E: 'ЕЭÉĔĚÊËĖÈĒĘ',
			Ye: 'Є',
			F: 'Ф',
			G: 'ГҐĞĜĢĠ',
			H: 'ĦĤ',
			I: 'ИІIÍĬÎÏİÌĪĮĨǏ',
			Yi: 'Ї',
			Ij: 'Ĳ',
			J: 'ЙĴ',
			K: 'КĶ',
			Kh: 'Х',
			L: 'ЛĹĽĻĿŁ',
			Lj: 'Љ',
			M: 'М',
			N: 'НŃŇŅÑ',
			Nj: 'Њ',
			Ng: 'Ŋ',
			O: 'ОÓŎÔÖÒŐŌØÕǾǑƠ',
			Yo: 'Ё',
			Oe: 'Œ',
			P: 'П',
			R: 'РŔŘŖ',
			S: 'СŚŠŞŜȘ',
			// SS: 'SS',
			Sh: 'Ш',
			Shch: 'Щ',
			T: 'ТŦŤŢȚ',
			Ts: 'Ц',
			Th: 'Þ',
			U: 'УÚŬÛÜÙŰŪŲŮŨǓǕǗǙǛƯ',
			Yu: 'Ю',
			V: 'В',
			W: 'ЎẂŴẄẀ',
			Y: 'ЫÝŶŸỲ',
			Z: 'ЗŹŽŻ',
			Zh: 'Ж',
			
			1: '¹',
			2: '²',
			3: '³',
			4: '⁴',
		};
		
		for (let key in transliterations) {
			const regex = new RegExp('[' + transliterations[key] + ']', 'g');
			string = string.replace(regex, key);
		}
		return string;
	},
	
};


/**
 * Get time elapsed since timestamp
 * @param {boolean} since_last
 * @return {int}
 */
function elapsed(since_last = true) {
	const current_time = Date.now();
	const elapsed = current_time - (since_last ? last_time : first_time);
	if (since_last) {
		last_time = current_time;
	}
	return elapsed;
}


/**
 * Get time elapsed since first timestamp
 * @return {int}
 */
function elapsed_first() {
	return elapsed(false);
}


/**
 * Get time elapsed since last timestamp
 * @return {int}
 */
function elapsed_last() {
	return elapsed(true);
}

